import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

const blank = {
  property_id: "", client_name: "", check_in_date: "", check_out_date: "",
  num_pax: "", booking_type: "", villa: "", status: "tentative",
  special_instructions: "", addon_experiences: [], custom_fields: {},
};

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}
  >
    {children}
  </button>
);

export const BookingFormDialog = ({ open, onOpenChange, config, editing, onSaved }) => {
  const { properties, selectedId } = useProperty();
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);

  const options = config?.options || {};
  const fields = config?.fields || [];

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setF({
        ...blank, ...editing,
        num_pax: editing.num_pax ?? "",
        addon_experiences: editing.addon_experiences || [],
        custom_fields: editing.custom_fields || {},
        special_instructions: editing.special_instructions || "",
        status: editing.status || "tentative",
        booking_type: editing.booking_type || "",
        villa: editing.villa || "",
      });
    } else {
      setF({ ...blank, property_id: selectedId || (properties[0]?.id || "") });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setCF = (k, v) => setF((s) => ({ ...s, custom_fields: { ...s.custom_fields, [k]: v } }));
  const toggleAddon = (key) => setF((s) => ({
    ...s,
    addon_experiences: s.addon_experiences.includes(key)
      ? s.addon_experiences.filter((x) => x !== key)
      : [...s.addon_experiences, key],
  }));
  const toggleCFMulti = (k, key) => setF((s) => {
    const cur = s.custom_fields[k] || [];
    const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key];
    return { ...s, custom_fields: { ...s.custom_fields, [k]: next } };
  });

  const onPickConfirmation = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/parse-confirmation", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((s) => {
        const next = { ...s };
        if (data.suggested_property_id && properties.some((p) => p.id === data.suggested_property_id)) {
          next.property_id = data.suggested_property_id;
        }
        if (data.client_name) next.client_name = data.client_name;
        if (data.check_in_date) next.check_in_date = data.check_in_date;
        if (data.check_out_date) next.check_out_date = data.check_out_date;
        if (data.num_pax !== null && data.num_pax !== undefined) next.num_pax = String(data.num_pax);
        if (data.status) next.status = data.status;
        if (data.booking_type) next.booking_type = data.booking_type;
        if (data.villa) next.villa = data.villa;
        if (Array.isArray(data.addon_experiences) && data.addon_experiences.length) {
          next.addon_experiences = Array.from(new Set([...s.addon_experiences, ...data.addon_experiences]));
        }
        if (data.special_instructions) next.special_instructions = data.special_instructions;
        return next;
      });
      const unmatched = data.unmatched || {};
      const unmatchedParts = [];
      ["booking_type", "villa", "status"].forEach((k) => {
        if (unmatched[k]) unmatchedParts.push(`${k.replace(/_/g, " ")}: "${unmatched[k]}"`);
      });
      if (unmatched.addon_experiences?.length) unmatchedParts.push(`add-ons: ${unmatched.addon_experiences.join(", ")}`);
      toast.success("Booking confirmation read — review the fields below.");
      if (unmatchedParts.length) {
        toast.warning(`Couldn't auto-match (add these in Configure fields if needed): ${unmatchedParts.join(" · ")}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't read the booking confirmation");
    } finally {
      setParsing(false);
    }
  };

  const submit = async () => {
    if (!f.property_id) return toast.error("Select a property");
    if (!f.client_name.trim()) return toast.error("Client name is required");
    if (!f.check_in_date || !f.check_out_date) return toast.error("Check-in and check-out dates are required");
    setSaving(true);
    try {
      const payload = {
        ...f,
        num_pax: f.num_pax === "" ? null : Number(f.num_pax),
        booking_type: f.booking_type || null,
        villa: f.villa || null,
      };
      let saved;
      if (editing) ({ data: saved } = await api.put(`/bookings/${editing.id}`, payload));
      else ({ data: saved } = await api.post("/bookings", payload));
      toast.success(editing ? "Booking updated" : "Booking created");
      onOpenChange(false);
      onSaved?.(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save booking");
    } finally {
      setSaving(false);
    }
  };

  const renderPicklist = (label, key, fieldKey) => (
    <div>
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Select value={f[key] || ""} onValueChange={(v) => set(key, v)}>
        <SelectTrigger className="mt-1" data-testid={`booking-${key}`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {(options[fieldKey] || []).map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const renderCustomField = (fd) => {
    const v = f.custom_fields[fd.key];
    if (fd.type === "textarea") {
      return <Textarea value={v || ""} onChange={(e) => setCF(fd.key, e.target.value)} className="mt-1" rows={2} data-testid={`booking-cf-${fd.key}`} />;
    }
    if (fd.type === "number") {
      return <Input type="number" value={v ?? ""} onChange={(e) => setCF(fd.key, e.target.value)} className="mt-1" data-testid={`booking-cf-${fd.key}`} />;
    }
    if (fd.type === "date") {
      return <Input type="date" value={v || ""} onChange={(e) => setCF(fd.key, e.target.value)} className="mt-1" data-testid={`booking-cf-${fd.key}`} />;
    }
    if (fd.type === "boolean") {
      return (
        <label className="mt-2 flex items-center gap-2 text-sm" data-testid={`booking-cf-${fd.key}`}>
          <Checkbox checked={!!v} onCheckedChange={(c) => setCF(fd.key, !!c)} /> Yes
        </label>
      );
    }
    if (fd.type === "select") {
      return (
        <Select value={v || ""} onValueChange={(val) => setCF(fd.key, val)}>
          <SelectTrigger className="mt-1" data-testid={`booking-cf-${fd.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {(fd.options || []).map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (fd.type === "multiselect") {
      return (
        <div className="mt-1 flex flex-wrap gap-2" data-testid={`booking-cf-${fd.key}`}>
          {(fd.options || []).map((o) => (
            <Chip key={o.key} active={(v || []).includes(o.key)} onClick={() => toggleCFMulti(fd.key, o.key)}>{o.label}</Chip>
          ))}
        </div>
      );
    }
    return <Input value={v || ""} onChange={(e) => setCF(fd.key, e.target.value)} className="mt-1" data-testid={`booking-cf-${fd.key}`} />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="booking-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit booking" : "New booking"}</DialogTitle>
          <DialogDescription>Capture the guest booking details. Fields marked with picklists are managed by your admin.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          {!editing && (
            <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="booking-confirmation-upload">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <Sparkles className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-800">Auto-fill from booking confirmation</div>
                  <div className="text-xs text-zinc-500">Upload a PDF, Word, image or text confirmation and we'll fill the details below. Payment details are never read.</div>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp,.heic,.heif"
                className="hidden"
                onChange={onPickConfirmation}
                data-testid="booking-confirmation-input"
              />
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                disabled={parsing}
                onClick={() => fileRef.current?.click()}
                data-testid="booking-confirmation-btn"
              >
                {parsing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {parsing ? "Reading…" : "Upload confirmation"}
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Property</Label>
              <Select value={f.property_id || ""} onValueChange={(v) => set("property_id", v)}>
                <SelectTrigger className="mt-1" data-testid="booking-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Client name</Label>
              <Input value={f.client_name} onChange={(e) => set("client_name", e.target.value)} className="mt-1" placeholder="Full name" data-testid="booking-client-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Check-in date</Label>
              <Input type="date" value={f.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} className="mt-1" data-testid="booking-checkin" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Check-out date</Label>
              <Input type="date" value={f.check_out_date} onChange={(e) => set("check_out_date", e.target.value)} className="mt-1" data-testid="booking-checkout" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">No. of pax</Label>
              <Input type="number" min="0" value={f.num_pax} onChange={(e) => set("num_pax", e.target.value)} className="mt-1" data-testid="booking-pax" />
            </div>
            {renderPicklist("Status", "status", "status")}
            {renderPicklist("Type of booking", "booking_type", "booking_type")}
            {renderPicklist("Villa allocated", "villa", "villa")}
          </div>

          <div>
            <Label className="text-xs text-zinc-500">Add-on experiences</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="booking-addons">
              {(options.addon_experience || []).length === 0 && <span className="text-xs text-zinc-400">No add-ons configured.</span>}
              {(options.addon_experience || []).map((o) => (
                <Chip key={o.key} active={f.addon_experiences.includes(o.key)} onClick={() => toggleAddon(o.key)} testid={`booking-addon-${o.key}`}>{o.label}</Chip>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-zinc-500">Special instructions</Label>
            <Textarea value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} className="mt-1" rows={2} placeholder="Dietary needs, arrival time, preferences…" data-testid="booking-special" />
          </div>

          {fields.length > 0 && (
            <div className="pt-2 border-t border-zinc-100">
              <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Additional details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map((fd) => (
                  <div key={fd.id} className={fd.type === "textarea" || fd.type === "multiselect" ? "sm:col-span-2" : ""}>
                    <Label className="text-xs text-zinc-500">{fd.label}{fd.required ? " *" : ""}</Label>
                    {renderCustomField(fd)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-zinc-950" data-testid="booking-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {editing ? "Save changes" : "Create booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
